"use client"

import { useState, useCallback } from "react"
import { toast } from "sonner"

interface UseClipboardOptions {
  timeout?: number
  successMessage?: string
}

export function useClipboard(options: UseClipboardOptions = {}) {
  const { timeout = 2000, successMessage = "Copied to clipboard" } = options
  const [copied, setCopied] = useState(false)
  const [copiedValue, setCopiedValue] = useState<string | null>(null)

  const copy = useCallback(
    async (text: string, customMessage?: string) => {
      try {
        await navigator.clipboard.writeText(text)
        setCopied(true)
        setCopiedValue(text)
        toast.success(customMessage || successMessage)

        setTimeout(() => {
          setCopied(false)
          setCopiedValue(null)
        }, timeout)

        return true
      } catch {
        toast.error("Failed to copy to clipboard")
        return false
      }
    },
    [successMessage, timeout]
  )

  return { copy, copied, copiedValue }
}

// Hook for tracking multiple copied states (e.g., for lists)
export function useClipboardMultiple(options: UseClipboardOptions = {}) {
  const { timeout = 2000, successMessage = "Copied to clipboard" } = options
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  const copy = useCallback(
    async (text: string, key: string, customMessage?: string) => {
      try {
        await navigator.clipboard.writeText(text)
        setCopiedKey(key)
        toast.success(customMessage || successMessage)

        setTimeout(() => {
          setCopiedKey(null)
        }, timeout)

        return true
      } catch {
        toast.error("Failed to copy to clipboard")
        return false
      }
    },
    [successMessage, timeout]
  )

  const isCopied = useCallback((key: string) => copiedKey === key, [copiedKey])

  return { copy, copiedKey, isCopied }
}
